import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { Users, Upload, FileText, Clock, Download, RefreshCw } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import ApiService, {
  RiepilogoDocumenti, DocumentoPersonale, EsitoImportMassivo, EsitoTimbrature,
  SchedaDipendente as SchedaDipendenteDati, AnagraficaDipendente
} from '../services/api';
import {
  PageContainerWide, PageHeader, Button, Card, CardHeader, CardBody,
  Alert, Badge, FormGroup, Label, Input, LoadingState
} from '../components/ui';

const MESI = ['', 'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
  'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'];

const dataItaliana = (iso: string | null): string =>
  iso ? new Date(iso).toLocaleDateString('it-IT') : '—';

const dimensione = (byte: number): string =>
  byte > 1024 * 1024 ? `${(byte / 1024 / 1024).toFixed(1)} MB` : `${Math.round(byte / 1024)} KB`;

// ============================================================
// Styled components locali (composizione: non hanno un
// equivalente fra i primitivi ui/)
// ============================================================
const Tabs = styled.div`
  display: flex;
  gap: var(--space-sm);
  border-bottom: 2px solid var(--gray-200);
  margin-bottom: var(--space-lg);
  overflow-x: auto;
`;

const Tab = styled.button<{ $attiva: boolean }>`
  background: none;
  border: none;
  border-bottom: 3px solid ${(p) => (p.$attiva ? 'var(--primary-600)' : 'transparent')};
  color: ${(p) => (p.$attiva ? 'var(--primary-700)' : 'var(--text-secondary)')};
  font-weight: ${(p) => (p.$attiva ? 'var(--font-weight-semibold)' : 'var(--font-weight-medium)')};
  font-family: var(--font-family);
  font-size: var(--font-size-sm);
  padding: var(--space-sm) var(--space-lg);
  cursor: pointer;
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: var(--space-sm);
  margin-bottom: -2px;

  &:hover { color: var(--primary-700); }
`;

/** Card con la spaziatura verticale della pagina. */
const Sezione = styled(Card)`
  margin-bottom: var(--space-lg);
`;

const Filtri = styled.div`
  display: flex;
  gap: var(--space-md);
  align-items: flex-end;
  flex-wrap: wrap;
  margin-bottom: var(--space-md);

  ${FormGroup} {
    margin-bottom: 0;
  }
`;

const TabellaScorrevole = styled.div`
  overflow-x: auto;
`;

const Tabella = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: var(--font-size-sm);

  th {
    text-align: left;
    padding: var(--space-sm) var(--space-md);
    border-bottom: 2px solid var(--gray-200);
    color: var(--text-secondary);
    font-weight: var(--font-weight-semibold);
    font-size: var(--font-size-xs);
    text-transform: uppercase;
    letter-spacing: 0.03em;
  }

  td {
    padding: var(--space-sm) var(--space-md);
    border-bottom: 1px solid var(--gray-100);
    vertical-align: middle;
  }

  tbody tr:hover { background: var(--gray-50); }
`;

const Numero = styled.td`
  text-align: right;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
`;

const Vuoto = styled.div`
  text-align: center;
  padding: var(--space-2xl) var(--space-md);
  color: var(--text-light);
`;

/** Scarica un documento passando dalla rotta autenticata. */
const scarica = async (documento: DocumentoPersonale) => {
  const blob = await ApiService.scaricaDocumento(documento.id);
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = documento.nome_file_originale;
  link.click();
  // L'URL temporaneo va rilasciato, altrimenti il blob resta in memoria
  URL.revokeObjectURL(url);
};

// ============================================================
// Scheda del dipendente
// ============================================================
const Griglia = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: var(--space-md);
  margin-bottom: var(--space-lg);
`;

const Riquadro = styled.div`
  padding: var(--space-md);
  border: 1px solid var(--gray-200);
  border-radius: var(--radius-md);
  background: var(--gray-50);

  span {
    display: block;
    font-size: var(--font-size-xs);
    text-transform: uppercase;
    letter-spacing: 0.03em;
    color: var(--text-secondary);
    margin-bottom: var(--space-xs);
  }

  strong {
    font-size: var(--font-size-lg);
    font-variant-numeric: tabular-nums;
  }

  small {
    display: block;
    color: var(--text-light);
    margin-top: var(--space-xs);
  }
`;

const CampiAnagrafica = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: var(--space-md);
  margin-bottom: var(--space-md);

  ${FormGroup} { margin-bottom: 0; }
`;

const euro = (valore: string | number | null): string => {
  if (valore === null || valore === undefined || valore === '') return '—';
  return Number(valore).toLocaleString('it-IT', {
    style: 'currency', currency: 'EUR', maximumFractionDigits: 0
  });
};

const numeroIt = (valore: string | number | null, decimali = 0): string =>
  valore === null || valore === undefined || valore === ''
    ? '—'
    : Number(valore).toLocaleString('it-IT', {
      minimumFractionDigits: decimali, maximumFractionDigits: decimali
    });

/** Da quanto tempo lavora qui, in una forma leggibile. */
const anzianita = (assunzione: string | null, cessazione: string | null): string => {
  if (!assunzione) return '—';
  const inizio = new Date(assunzione);
  const fine = cessazione ? new Date(cessazione) : new Date();
  const mesi = Math.max(0, Math.round((fine.getTime() - inizio.getTime()) / (1000 * 60 * 60 * 24 * 30.44)));
  const anni = Math.floor(mesi / 12);
  const resto = mesi % 12;
  const testo = anni
    ? `${anni} ${anni === 1 ? 'anno' : 'anni'}${resto ? ` e ${resto} mesi` : ''}`
    : `${mesi} ${mesi === 1 ? 'mese' : 'mesi'}`;
  return cessazione ? `${testo} (cessato)` : testo;
};

const CAMPI: Array<{ chiave: string; etichetta: string; tipo?: string }> = [
  { chiave: 'nome', etichetta: 'Nome' },
  { chiave: 'cognome', etichetta: 'Cognome' },
  { chiave: 'codiceFiscale', etichetta: 'Codice fiscale' },
  { chiave: 'email', etichetta: 'Email', tipo: 'email' },
  { chiave: 'telefono', etichetta: 'Cellulare', tipo: 'tel' },
  { chiave: 'matricola', etichetta: 'Matricola' },
  { chiave: 'dataNascita', etichetta: 'Data di nascita', tipo: 'date' },
  { chiave: 'dataAssunzione', etichetta: 'Assunzione', tipo: 'date' },
  { chiave: 'dataCessazione', etichetta: 'Cessazione', tipo: 'date' }
];

const DA_ANAGRAFICA: Record<string, keyof AnagraficaDipendente> = {
  nome: 'nome', cognome: 'cognome', codiceFiscale: 'codice_fiscale', email: 'email',
  telefono: 'telefono', matricola: 'matricola', dataNascita: 'data_nascita',
  dataAssunzione: 'data_assunzione', dataCessazione: 'data_cessazione'
};

/** Le date arrivano come timestamp ISO ma l'input HTML vuole solo il giorno. */
const perInput = (valore: unknown): string =>
  typeof valore === 'string' ? valore.slice(0, 10) : (valore ?? '') as string;

const SchedaDipendente: React.FC<{ utenteId: number; onChiudi: () => void }> = ({
  utenteId, onChiudi
}) => {
  const [scheda, setScheda] = useState<SchedaDipendenteDati | null>(null);
  const [documenti, setDocumenti] = useState<DocumentoPersonale[]>([]);
  const [modifiche, setModifiche] = useState<Record<string, string>>({});
  const [messaggio, setMessaggio] = useState<{ tipo: 'success' | 'error'; testo: string } | null>(null);
  const [inCorso, setInCorso] = useState(false);

  const carica = useCallback(async () => {
    const [s, d] = await Promise.all([
      ApiService.getSchedaDipendente(utenteId),
      ApiService.getDocumentiUtente(utenteId)
    ]);
    setScheda(s);
    setDocumenti(d);
    setModifiche({});
  }, [utenteId]);

  useEffect(() => { carica(); }, [carica]);

  if (!scheda) return <Sezione><CardBody><LoadingState /></CardBody></Sezione>;

  const { utente, totali, perAnno, buste, turni } = scheda;
  const valore = (chiave: string): string =>
    chiave in modifiche ? modifiche[chiave] : perInput(utente[DA_ANAGRAFICA[chiave]]);

  const salva = async () => {
    if (!Object.keys(modifiche).length) return;
    setInCorso(true);
    setMessaggio(null);
    try {
      await ApiService.salvaAnagraficaDipendente(utenteId, modifiche);
      await carica();
      setMessaggio({ tipo: 'success', testo: 'Anagrafica salvata.' });
    } catch (e: any) {
      setMessaggio({ tipo: 'error', testo: e?.response?.data?.error || 'Errore nel salvataggio' });
    } finally {
      setInCorso(false);
    }
  };

  const calcolaCostoOrario = async () => {
    setInCorso(true);
    setMessaggio(null);
    try {
      const esito = await ApiService.costoOrarioDaBuste(utenteId, 3, true);
      await carica();
      setMessaggio({
        tipo: 'success',
        testo: `Costo orario aggiornato a ${Number(esito.costoOrario).toFixed(2)} €, `
          + `media delle ultime ${esito.buste.length} buste paga.`
      });
    } catch (e: any) {
      setMessaggio({
        tipo: 'error',
        testo: e?.response?.data?.motivo || e?.response?.data?.error
          || 'Non è stato possibile calcolare il costo orario'
      });
    } finally {
      setInCorso(false);
    }
  };

  const nomeCompleto = [utente.nome, utente.cognome].filter(Boolean).join(' ');

  return (
    <Sezione>
      <CardHeader>
        <h3>{nomeCompleto}</h3>
        <Button $variant="secondary" $size="sm" onClick={onChiudi}>Chiudi</Button>
      </CardHeader>
      <CardBody>
        <Griglia>
          <Riquadro>
            <span>In azienda da</span>
            <strong>{anzianita(utente.data_assunzione, utente.data_cessazione)}</strong>
            <small>{utente.data_assunzione ? `dal ${dataItaliana(utente.data_assunzione)}` : 'data di assunzione non inserita'}</small>
          </Riquadro>
          <Riquadro>
            <span>Netto percepito</span>
            <strong>{euro(totali.totale_netto)}</strong>
            <small>{totali.buste} buste paga{totali.primo_anno ? ` dal ${totali.primo_anno}` : ''}</small>
          </Riquadro>
          <Riquadro>
            <span>Lordo</span>
            <strong>{euro(totali.totale_lordo)}</strong>
            <small>competenze totali a cedolino</small>
          </Riquadro>
          <Riquadro>
            <span>TFR maturato</span>
            <strong>{euro(totali.tfr_maturato)}</strong>
            <small>solo dai cedolini che lo riportano</small>
          </Riquadro>
          <Riquadro>
            <span>Ore retribuite</span>
            <strong>{numeroIt(totali.ore, 0)}</strong>
            <small>{turni.turni ? `${numeroIt(turni.ore_timbrate, 0)} ore timbrate` : 'nessuna timbratura importata'}</small>
          </Riquadro>
          <Riquadro>
            <span>Costo orario</span>
            <strong>{utente.costo_orario ? `${Number(utente.costo_orario).toFixed(2)} €` : '—'}</strong>
            <small>
              <Button $variant="secondary" $size="sm" onClick={calcolaCostoOrario} disabled={inCorso}>
                <RefreshCw /> Calcola dalle buste paga
              </Button>
            </small>
          </Riquadro>
        </Griglia>

        {messaggio && <Alert $variant={messaggio.tipo}>{messaggio.testo}</Alert>}

        {totali.buste_da_verificare > 0 && (
          <Alert $variant="warning">
            {totali.buste_da_verificare} cedolini non quadrano fra competenze, trattenute e
            netto: restano fuori dai totali finché non li si guarda.
          </Alert>
        )}

        <h4>Anagrafica</h4>
        <CampiAnagrafica>
          {CAMPI.map((campo) => (
            <FormGroup key={campo.chiave}>
              <Label htmlFor={`campo-${campo.chiave}`}>{campo.etichetta}</Label>
              <Input id={`campo-${campo.chiave}`} type={campo.tipo || 'text'}
                value={valore(campo.chiave)}
                onChange={(e) => setModifiche((m) => ({ ...m, [campo.chiave]: e.target.value }))} />
            </FormGroup>
          ))}
        </CampiAnagrafica>
        <Button $variant="primary" onClick={salva} disabled={!Object.keys(modifiche).length || inCorso}>
          Salva anagrafica
        </Button>

        {!utente.codice_fiscale && (
          <Alert $variant="info">
            Senza codice fiscale le buste paga si abbinano solo per nome, che è la ragione
            per cui alcune non venivano attribuite. Caricando un cedolino il codice fiscale
            viene letto dal PDF e compilato da solo.
          </Alert>
        )}

        {!!perAnno.length && (
          <>
            <h4>Per anno</h4>
            <TabellaScorrevole>
              <Tabella>
                <thead>
                  <tr>
                    <th>Anno</th>
                    <th style={{ textAlign: 'right' }}>Buste</th>
                    <th style={{ textAlign: 'right' }}>Netto</th>
                    <th style={{ textAlign: 'right' }}>Lordo</th>
                    <th style={{ textAlign: 'right' }}>TFR</th>
                    <th style={{ textAlign: 'right' }}>Ore</th>
                    <th style={{ textAlign: 'right' }}>Paga oraria</th>
                  </tr>
                </thead>
                <tbody>
                  {perAnno.map((a) => (
                    <tr key={a.anno}>
                      <td>{a.anno}</td>
                      <Numero>{a.buste}</Numero>
                      <Numero>{euro(a.netto)}</Numero>
                      <Numero>{euro(a.lordo)}</Numero>
                      <Numero>{euro(a.tfr)}</Numero>
                      <Numero>{numeroIt(a.ore, 0)}</Numero>
                      <Numero>{a.retribuzione_oraria ? `${Number(a.retribuzione_oraria).toFixed(2)} €` : '—'}</Numero>
                    </tr>
                  ))}
                </tbody>
              </Tabella>
            </TabellaScorrevole>
          </>
        )}

        {!!buste.length && (
          <>
            <h4>Buste paga</h4>
            <TabellaScorrevole>
              <Tabella>
                <thead>
                  <tr>
                    <th>Periodo</th>
                    <th style={{ textAlign: 'right' }}>Competenze</th>
                    <th style={{ textAlign: 'right' }}>Trattenute</th>
                    <th style={{ textAlign: 'right' }}>Netto</th>
                    <th style={{ textAlign: 'right' }}>Ore</th>
                    <th style={{ textAlign: 'right' }}>Paga oraria</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {buste.map((b) => (
                    <tr key={b.id}>
                      <td>
                        {b.mese ? `${MESI[b.mese]} ${b.anno}` : `${b.tipo_mensilita} ${b.anno}`}
                      </td>
                      <Numero>{euro(b.totale_competenze)}</Numero>
                      <Numero>{euro(b.totale_trattenute)}</Numero>
                      <Numero>{euro(b.netto)}</Numero>
                      <Numero>{numeroIt(b.ore_ordinarie, 0)}</Numero>
                      <Numero>{b.retribuzione_oraria ? `${Number(b.retribuzione_oraria).toFixed(2)} €` : '—'}</Numero>
                      <td style={{ textAlign: 'right' }}>
                        {b.quadra === false && <Badge $variant="warning">non quadra</Badge>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Tabella>
            </TabellaScorrevole>
          </>
        )}

        <h4>Documenti</h4>
        {!documenti.length && <Vuoto>Nessun documento.</Vuoto>}
        {!!documenti.length && (
          <TabellaScorrevole>
            <Tabella>
              <thead>
                <tr><th>Documento</th><th>Periodo</th><th>Caricato</th><th>Letto</th><th /></tr>
              </thead>
              <tbody>
                {documenti.map((d) => (
                  <tr key={d.id}>
                    <td>{d.titolo}</td>
                    <td>{d.anno ? `${d.mese ? MESI[d.mese] + ' ' : ''}${d.anno}` : '—'}</td>
                    <td>{dataItaliana(d.caricato_at)}</td>
                    <td>{d.visto_dal_dipendente_at ? dataItaliana(d.visto_dal_dipendente_at) : 'no'}</td>
                    <td style={{ textAlign: 'right' }}>
                      <Button $variant="secondary" $size="sm" onClick={() => scarica(d)}>
                        <Download /> Scarica
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Tabella>
          </TabellaScorrevole>
        )}
      </CardBody>
    </Sezione>
  );
};

// ============================================================
// Archivio documenti (vista di gestione)
// ============================================================
const ArchivioTab: React.FC = () => {
  const [riepilogo, setRiepilogo] = useState<RiepilogoDocumenti[]>([]);
  const [aperto, setAperto] = useState<RiepilogoDocumenti | null>(null);
  const [files, setFiles] = useState<File[]>([]);
  const [esito, setEsito] = useState<EsitoImportMassivo | null>(null);
  const [inCorso, setInCorso] = useState(false);
  const [errore, setErrore] = useState<string | null>(null);

  const carica = useCallback(async () => {
    setRiepilogo(await ApiService.getRiepilogoDocumenti());
  }, []);

  useEffect(() => { carica(); }, [carica]);

  const apri = (persona: RiepilogoDocumenti) => setAperto(persona);

  const importa = async () => {
    if (!files.length) return;
    setInCorso(true); setErrore(null); setEsito(null);
    try {
      setEsito(await ApiService.importaDocumentiMassivo(files));
      setFiles([]);
      await carica();
    } catch (e: any) {
      setErrore(e?.response?.data?.error || 'Errore nel caricamento');
    } finally {
      setInCorso(false);
    }
  };

  return (
    <>
      <Alert $variant="info">
        Puoi selezionare l'intero archivio in una volta: il dipendente viene riconosciuto
        dal codice fiscale stampato sul cedolino, o dal nome del file quando non c'è, e i file vengono elaborati uno alla volta. Ricaricare l'archivio
        una seconda volta è innocuo, i documenti già presenti non vengono duplicati.
      </Alert>

      <Sezione>
        <CardHeader><h3>Caricamento massivo</h3></CardHeader>
        <CardBody>
          <Filtri>
            <FormGroup>
              <Label htmlFor="doc">Documenti (PDF)</Label>
              <Input id="doc" type="file" accept="application/pdf,image/png,image/jpeg" multiple
                onChange={(e) => setFiles(Array.from(e.target.files || []))} />
            </FormGroup>
            <Button $variant="primary" onClick={importa} disabled={!files.length || inCorso}>
              <Upload />
              {inCorso ? 'Caricamento…' : `Carica${files.length ? ` ${files.length} file` : ''}`}
            </Button>
          </Filtri>

          {errore && <Alert $variant="error">{errore}</Alert>}

          {esito && (
            <>
              <Alert $variant="success">
                {esito.file} file elaborati: <strong>{esito.caricati}</strong> caricati
                {esito.giaPresenti > 0 && <>, {esito.giaPresenti} già presenti</>}.
                {esito.cedoliniLetti > 0 && (
                  <> Di <strong>{esito.cedoliniLetti}</strong> buste paga sono stati letti anche
                    gli importi, che finiscono nella scheda del dipendente.</>
                )}
              </Alert>

              {!!esito.perDipendente.length && (
                <TabellaScorrevole>
                  <Tabella>
                    <tbody>
                      {esito.perDipendente.map((d) => (
                        <tr key={d.utenteId}>
                          <td>{d.nome}</td>
                          <Numero>{d.caricati} caricati</Numero>
                          <Numero style={{ color: 'var(--text-light)' }}>
                            {d.giaPresenti > 0 ? `${d.giaPresenti} già presenti` : ''}
                          </Numero>
                        </tr>
                      ))}
                    </tbody>
                  </Tabella>
                </TabellaScorrevole>
              )}

              {!!esito.nonAbbinati.length && (
                <Alert $variant="warning">
                  <strong>{esito.nonAbbinati.length} file non attribuiti a nessuno.</strong>{' '}
                  Non vengono assegnati a caso: significherebbe mettere la busta paga di una
                  persona nell'archivio di un'altra. Di solito è un dipendente che non è in
                  anagrafica: aggiungilo e ricarica.
                  <ul style={{ margin: 'var(--space-sm) 0 0', paddingLeft: '1.2rem' }}>
                    {esito.nonAbbinati.slice(0, 12).map((n) => (
                      <li key={n.file}>
                        {n.nomeLetto || n.file}
                        {n.codiceFiscaleLetto && <> (CF {n.codiceFiscaleLetto})</>} — {n.motivo}
                      </li>
                    ))}
                  </ul>
                </Alert>
              )}
            </>
          )}
        </CardBody>
      </Sezione>

      <Sezione>
        <CardHeader><h3>Documenti per dipendente</h3></CardHeader>
        <CardBody>
          {!riepilogo.length && <Vuoto>Nessun dipendente in anagrafica.</Vuoto>}
          {!!riepilogo.length && (
            <TabellaScorrevole>
              <Tabella>
                <thead>
                  <tr>
                    <th>Dipendente</th>
                    <th style={{ textAlign: 'right' }}>Documenti</th>
                    <th style={{ textAlign: 'right' }}>Buste paga</th>
                    <th style={{ textAlign: 'right' }}>Non letti</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {riepilogo.map((p) => (
                    <tr key={p.id}>
                      <td>
                        {[p.nome, p.cognome].filter(Boolean).join(' ')}
                        {!p.attivo && <> <Badge $variant="neutral">cessato</Badge></>}
                      </td>
                      <Numero>{p.documenti}</Numero>
                      <Numero>{p.buste_paga}</Numero>
                      <Numero>{p.non_letti}</Numero>
                      <td style={{ textAlign: 'right' }}>
                        <Button $variant="secondary" $size="sm" onClick={() => apri(p)}>Apri</Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Tabella>
            </TabellaScorrevole>
          )}
        </CardBody>
      </Sezione>

      {aperto && (
        <SchedaDipendente
          key={aperto.id}
          utenteId={aperto.id}
          onChiudi={() => { setAperto(null); carica(); }}
        />
      )}
    </>
  );
};

// ============================================================
// I miei documenti (vista del dipendente)
// ============================================================
const MieiDocumentiTab: React.FC = () => {
  const [documenti, setDocumenti] = useState<DocumentoPersonale[]>([]);
  const [caricamento, setCaricamento] = useState(true);

  useEffect(() => {
    ApiService.getMieiDocumenti()
      .then(setDocumenti)
      .finally(() => setCaricamento(false));
  }, []);

  if (caricamento) return <Sezione><LoadingState label="Caricamento…" /></Sezione>;

  return (
    <Sezione>
      <CardBody>
        {!documenti.length && (
          <Vuoto>
            <FileText size={32} style={{ opacity: 0.4 }} />
            <p>Non ci sono ancora documenti nel tuo archivio.</p>
          </Vuoto>
        )}
        {!!documenti.length && (
          <TabellaScorrevole>
            <Tabella>
              <thead>
                <tr><th>Documento</th><th>Periodo</th><th>Dimensione</th><th /></tr>
              </thead>
              <tbody>
                {documenti.map((d) => (
                  <tr key={d.id}>
                    <td>{d.titolo}</td>
                    <td>{d.anno ? `${d.mese ? MESI[d.mese] + ' ' : ''}${d.anno}` : '—'}</td>
                    <td style={{ color: 'var(--text-secondary)' }}>{dimensione(d.dimensione_byte)}</td>
                    <td style={{ textAlign: 'right' }}>
                      <Button $variant="secondary" $size="sm" onClick={() => scarica(d)}>
                        <Download /> Scarica
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Tabella>
          </TabellaScorrevole>
        )}
      </CardBody>
    </Sezione>
  );
};

// ============================================================
// Timbrature e ore consuntive
// ============================================================
const TimbratureTab: React.FC = () => {
  const [files, setFiles] = useState<File[]>([]);
  const [esito, setEsito] = useState<EsitoTimbrature | null>(null);
  const [inCorso, setInCorso] = useState(false);
  const [errore, setErrore] = useState<string | null>(null);

  const anno = new Date().getFullYear();
  const [periodo, setPeriodo] = useState({ da: `${anno}-01-01`, a: `${anno}-12-31` });
  const [ore, setOre] = useState<any[]>([]);

  const caricaOre = useCallback(async () => {
    setOre(await ApiService.getOreConsuntive(periodo.da, periodo.a));
  }, [periodo]);

  useEffect(() => { caricaOre(); }, [caricaOre]);

  const importa = async () => {
    if (!files.length) return;
    setInCorso(true); setErrore(null); setEsito(null);
    try {
      setEsito(await ApiService.importaTimbrature(files));
      setFiles([]);
      await caricaOre();
    } catch (e: any) {
      setErrore(e?.response?.data?.error || "Errore nell'import delle timbrature");
    } finally {
      setInCorso(false);
    }
  };

  const senzaCosto = ore.some((r) => !r.costoAttendibile);

  return (
    <>
      <Alert $variant="info">
        Le timbrature dicono cosa è successo davvero e diventano turni consuntivi, distinti
        da quelli pianificati. L'abbinamento passa dal codice fiscale, non dal nome: nei
        fogli dell'export lo stesso dipendente compare scritto in ordini diversi.
      </Alert>

      <Sezione>
        <CardHeader><h3>Import timbrature</h3></CardHeader>
        <CardBody>
          <Filtri>
            <FormGroup>
              <Label htmlFor="tmb">File Excel, uno per anno</Label>
              <Input id="tmb" type="file" multiple
                accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                onChange={(e) => setFiles(Array.from(e.target.files || []))} />
            </FormGroup>
            <Button $variant="primary" onClick={importa} disabled={!files.length || inCorso}>
              <Upload />
              {inCorso ? 'Import…' : `Importa${files.length ? ` ${files.length} file` : ''}`}
            </Button>
          </Filtri>

          {errore && <Alert $variant="error">{errore}</Alert>}

          {esito && (
            <>
              <Alert $variant="success">
                {esito.totali.lette} timbrature lette da {esito.file} file,{' '}
                <strong>{esito.totali.importate}</strong> importate
                {esito.totali.duplicate > 0 && <>, {esito.totali.duplicate} già presenti</>}.
                {esito.totali.scartate > 0 && (
                  <> {esito.totali.scartate} scartate perché scavalcano la mezzanotte: la
                    tabella dei turni non le accetta, e forzarle con un orario inventato
                    falserebbe le ore.</>
                )}
              </Alert>

              {!!esito.senzaDipendente.length && (
                <Alert $variant="warning">
                  Timbrature di persone che non risultano in anagrafica, o senza codice
                  fiscale registrato:{' '}
                  {esito.senzaDipendente.map((p) => `${p.dipendente} (${p.timbrature})`).join(', ')}.
                  Non vengono attribuite a nessuno.
                </Alert>
              )}
            </>
          )}
        </CardBody>
      </Sezione>

      <Sezione>
        <CardHeader><h3>Ore lavorate</h3></CardHeader>
        <CardBody>
          <Filtri>
            <FormGroup>
              <Label htmlFor="ore-da">Dal</Label>
              <Input id="ore-da" type="date" value={periodo.da}
                onChange={(e) => setPeriodo({ ...periodo, da: e.target.value })} />
            </FormGroup>
            <FormGroup>
              <Label htmlFor="ore-a">Al</Label>
              <Input id="ore-a" type="date" value={periodo.a}
                onChange={(e) => setPeriodo({ ...periodo, a: e.target.value })} />
            </FormGroup>
            <Button $variant="secondary" onClick={caricaOre}><RefreshCw /> Aggiorna</Button>
          </Filtri>

          {senzaCosto && (
            <Alert $variant="warning">
              Per alcune persone il costo orario non è impostato in anagrafica: per loro la
              colonna del costo vale zero e non è un dato reale.
            </Alert>
          )}

          {!ore.length && <Vuoto>Nessuna timbratura nel periodo.</Vuoto>}
          {!!ore.length && (
            <TabellaScorrevole>
              <Tabella>
                <thead>
                  <tr>
                    <th>Dipendente</th>
                    <th style={{ textAlign: 'right' }}>Turni</th>
                    <th style={{ textAlign: 'right' }}>Ore</th>
                    <th style={{ textAlign: 'right' }}>Costo orario</th>
                    <th style={{ textAlign: 'right' }}>Costo</th>
                  </tr>
                </thead>
                <tbody>
                  {ore.map((r) => (
                    <tr key={r.utenteId}>
                      <td>{r.nome}</td>
                      <Numero>{r.turni}</Numero>
                      <Numero>{r.ore.toLocaleString('it-IT')}</Numero>
                      <Numero style={{ color: r.costoAttendibile ? undefined : 'var(--text-light)', fontStyle: r.costoAttendibile ? undefined : 'italic' }}>
                        {r.costoOrario ? `${r.costoOrario} €` : 'non impostato'}
                      </Numero>
                      <Numero style={{ color: r.costoAttendibile ? undefined : 'var(--text-light)', fontStyle: r.costoAttendibile ? undefined : 'italic' }}>
                        {r.costoAttendibile
                          ? r.costo.toLocaleString('it-IT', { style: 'currency', currency: 'EUR' })
                          : '—'}
                      </Numero>
                    </tr>
                  ))}
                </tbody>
              </Tabella>
            </TabellaScorrevole>
          )}
        </CardBody>
      </Sezione>
    </>
  );
};

// ============================================================
// Pagina
// ============================================================
type Scheda = 'miei' | 'archivio' | 'timbrature';

const PersonalePage: React.FC = () => {
  const { user } = useAuth();
  // Archivio e timbrature sono funzioni tecniche: solo admin.
  const gestisce = user?.ruolo === 'admin';
  const [scheda, setScheda] = useState<Scheda>(gestisce ? 'archivio' : 'miei');

  return (
    <PageContainerWide>
      <PageHeader title="Personale" icon={<Users />} />

      <Tabs>
        <Tab $attiva={scheda === 'miei'} onClick={() => setScheda('miei')}>
          <FileText size={16} /> I miei documenti
        </Tab>
        {gestisce && (
          <>
            <Tab $attiva={scheda === 'archivio'} onClick={() => setScheda('archivio')}>
              <Users size={16} /> Archivio
            </Tab>
            <Tab $attiva={scheda === 'timbrature'} onClick={() => setScheda('timbrature')}>
              <Clock size={16} /> Timbrature
            </Tab>
          </>
        )}
      </Tabs>

      {scheda === 'miei' && <MieiDocumentiTab />}
      {scheda === 'archivio' && gestisce && <ArchivioTab />}
      {scheda === 'timbrature' && gestisce && <TimbratureTab />}
    </PageContainerWide>
  );
};

export default PersonalePage;
